import { useGetList, useRedirect } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { Flame, Sun, Snowflake, User } from "lucide-react";
import type { Contact } from "../types";
import { formatDistanceToNow } from "date-fns";

/**
 * Lead Temperature Radar
 * A visual heatmap of Hot/Warm/Cold leads.
 * Replaces the static list with a vibrant, categorized grid.
 */
export const LeadTemperatureRadar = () => {
    const redirect = useRedirect();

    // Fetch HOT contacts
    const { data: hotContacts, isPending: hotPending } = useGetList<Contact>("contacts", {
        pagination: { page: 1, perPage: 5 },
        sort: { field: "last_seen", order: "DESC" },
        filter: { status: "hot" }
    });

    // Fetch WARM contacts
    const { data: warmContacts, isPending: warmPending } = useGetList<Contact>("contacts", {
        pagination: { page: 1, perPage: 5 },
        sort: { field: "last_seen", order: "DESC" },
        filter: { status: "warm" }
    });

    // Fetch COLD contacts
    const { data: coldContacts, isPending: coldPending } = useGetList<Contact>("contacts", {
        pagination: { page: 1, perPage: 5 },
        sort: { field: "last_seen", order: "DESC" },
        filter: { status: "cold" }
    });

    const handleContactClick = (id: string) => {
        redirect(`/contacts/${id}/show`);
    };

    if (hotPending || warmPending || coldPending) return null;

    // Helper to render a column
    const renderColumn = (title: string, icon: React.ReactNode, contacts: Contact[] = [], colorClass: string, badgeClass: string) => (
        <div className="flex flex-col gap-3 min-w-[200px] flex-1">
            <div className={cn("flex items-center gap-2 font-semibold pb-2 border-b-2", colorClass)}>
                {icon}
                <span>{title}</span>
                <Badge variant="secondary" className="ml-auto text-xs">
                    {contacts.length}
                </Badge>
            </div>

            <div className="space-y-2">
                {contacts.length === 0 && (
                    <div className="text-sm text-slate-400 italic py-4 text-center bg-slate-50/50 dark:bg-slate-900/20 rounded-lg border-2 border-dashed border-slate-100 dark:border-slate-800">
                        No {title.toLowerCase()} leads
                    </div>
                )}

                {contacts.map((contact) => (
                    <div
                        key={contact.id}
                        onClick={() => handleContactClick(String(contact.id))}
                        className="group relative bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-100 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-600 hover:shadow-md transition-all cursor-pointer flex items-center gap-3 overflow-hidden"
                    >
                        {/* Status Line Indicator */}
                        <div className={cn("absolute left-0 top-0 bottom-0 w-1", badgeClass)} />

                        <Avatar className="h-9 w-9 border border-slate-100">
                            <AvatarImage src={contact.avatar?.src} />
                            <AvatarFallback className="text-xs bg-slate-100 text-slate-500">
                                {contact.first_name?.[0]}{contact.last_name?.[0]}
                            </AvatarFallback>
                        </Avatar>

                        <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate group-hover:text-indigo-600 transition-colors">
                                {contact.first_name} {contact.last_name}
                            </h4>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                                <User className="w-3 h-3" />
                                <span className="truncate">{contact.title || "Contact"}</span>
                            </div>
                        </div>

                        {/* Time or Pulse */}
                        <div className="text-[10px] text-slate-400 whitespace-nowrap">
                            {contact.last_seen ? formatDistanceToNow(new Date(contact.last_seen), { addSuffix: true }).replace("about ", "") : ""}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <Card className="border-0 shadow-sm bg-gradient-to-b from-white to-slate-50 dark:from-slate-900 dark:to-slate-950">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                    <Flame className="w-5 h-5 text-orange-500" />
                    Lead Temperature Radar
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {renderColumn(
                        "Hot / Urgent",
                        <Flame className="w-4 h-4 text-red-500" />,
                        hotContacts,
                        "border-red-500/50 text-red-700 dark:text-red-400",
                        "bg-red-500"
                    )}

                    {renderColumn(
                        "Warm / Nurture",
                        <Sun className="w-4 h-4 text-orange-400" />,
                        warmContacts,
                        "border-orange-400/50 text-orange-700 dark:text-orange-400",
                        "bg-orange-400"
                    )}

                    {renderColumn(
                        "Cold / Sleeping",
                        <Snowflake className="w-4 h-4 text-blue-400" />,
                        coldContacts,
                        "border-blue-400/50 text-blue-700 dark:text-blue-400",
                        "bg-blue-400"
                    )}
                </div>
            </CardContent>
        </Card>
    );
};
